import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    ContractDTO,
    useDeleteContractApi,
    useSaveContractApi,
    useSearchContractApi,
} from '../../../api/contract-api';
import SvgAdd from '../../../assets/icons/Add';

import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { CreatedRowInfo } from '../../../components/table/created-row-info';
import { ModifiedRowInfo } from '../../../components/table/modified-row-info';
import { DateCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { calculateContractPeriod } from './contract-helper';
import { SaveContractModal } from './save-contract-modal';

export function ContractApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedContract, setSelectedContract] = useState<ContractDTO | undefined>();
    const contractInit = useSearchContractApi();
    const saveContract = useSaveContractApi();
    const deleteContract = useDeleteContractApi();

    const { result, error, busy, initialized, initializing } = contractInit;

    const boardTitle = useCallback(
        (id?: number) => {
            var board = result?.boards.find(x => x.id === id);
            return <span>{board ? `${board.title} ` : id}</span>;
        },
        [result?.boards]
    );

    const customerTitle = useCallback(
        (id?: number) => {
            var customer = result?.customers.find(x => x.id === id);
            return (
                <bd.Flex align="center" gap={2}>
                    <span>{customer ? `${customer.firstName} ${customer.lastName} ` : id}</span>
                    {customer && customer.companyName && (
                        <span className="small text-muted">{`(${customer.companyName})`}</span>
                    )}
                </bd.Flex>
            );
        },
        [result?.customers]
    );

    const columns: IBasicTableColumn<ContractDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 20,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>board-id</T>,
                name: 'boardId',
                Cell: ({ row }) => boardTitle(row.boardId),
            },
            {
                Header: <T>customer-id</T>,
                name: 'customerId',
                Cell: ({ row }) => customerTitle(row.customerId),
            },
            {
                Header: <T>start-date</T>,
                name: 'startDate',
                width: 150,
                Cell: DateCell,
            },
            {
                Header: <T>end-date</T>,
                name: 'endDate',
                width: 150,
                Cell: DateCell,
            },
            {
                Header: <T>contract-period</T>,
                name: 'endDate',
                width: 120,
                Cell: ({ row }: { row: any }) =>
                    calculateContractPeriod(row.startDate, row.endDate),
            },
            {
                Header: <T>price</T>,
                name: 'price',
                width: 80,
                Cell: ({ row }) => <N>{row.price}</N>,
            },

            {
                Header: <T>created-by</T>,
                name: 'createdBy',
                width: 100,
                Cell: CreatedRowInfo,
            },
            {
                Header: <T>modified-by</T>,
                name: 'modifiedBy',
                width: 100,
                Cell: ModifiedRowInfo,
            },
        ],
        [boardTitle, customerTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        contractInit.post(null, {});
    }, [initialized, initializing, error, contractInit]);

    const handleDelete = (contract: ContractDTO) => {
        bd.msgbox(
            translate('delete-contract'),
            translate('confirm-delete-contract'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteContract.remove(
                            contract,
                            () => {
                                contractInit.updateResult({
                                    ...result,
                                    contracts: result.contracts.filter(b => b.id !== contract.id),
                                });
                                notify.dark(translate('selected-contract-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedContract(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (contract: ContractDTO) => {
        saveContract.post(
            null,
            contract,
            res => {
                if (selectedContract) {
                    contractInit.updateResult({
                        ...result,
                        contracts: result.contracts.map(b =>
                            b.id === contract.id ? res.contract : b
                        ),
                    });
                } else {
                    contractInit.updateResult({
                        ...result,
                        contracts: [res.contract, ...result.contracts],
                    });
                }

                setShowModal(false);
                setSelectedContract(undefined);
                notify.success(
                    selectedContract
                        ? translate('selected-contract-updated-successfully')
                        : translate('new-contract-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => contractInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="contract"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-contract</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedContract(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.contracts}
                        onRowClick={({ row }) => {
                            setSelectedContract(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveContractModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedContract(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedContract={selectedContract}
                        boards={result?.boards}
                        customers={result?.customers}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
